import { useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  BellOff,
  Building2,
  Mail,
  MoreVertical,
  ShieldOff,
  Volume2,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { MessageComposer } from "@/components/messages/MessageComposer";
import { MessageList } from "@/components/messages/MessageList";
import { RoleChip } from "@/components/messages/ConversationRow";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import {
  messageErrorMessage,
  useBlockMessageUser,
  useMessageConversation,
  useSendMessage,
  useUpdateMessageConversation,
} from "@/lib/messages";
import "@/styles/messages.css";

export default function MessagesConversation() {
  const { city } = useCity();
  const { id = "" } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const detail = useMessageConversation(city.slug, id);
  const send = useSendMessage(city.slug, id);
  const updateConversation = useUpdateMessageConversation(city.slug);
  const blockUser = useBlockMessageUser(city.slug);
  const [menuOpen, setMenuOpen] = useState(false);
  const markedRead = useRef<string | null>(null);
  const conversation = detail.data?.pages[0]?.conversation;
  const messages = useMemo(() => {
    if (!detail.data) return [];
    const seen = new Set<string>();
    return [...detail.data.pages]
      .reverse()
      .flatMap((page) => page.messages)
      .filter((message) => {
        if (seen.has(message.id)) return false;
        seen.add(message.id);
        return true;
      });
  }, [detail.data]);

  useEffect(() => {
    if (!conversation || markedRead.current === conversation.id) return;
    markedRead.current = conversation.id;
    void queryClient.invalidateQueries({ queryKey: ["messages-unread", city.slug] });
    void queryClient.invalidateQueries({ queryKey: ["messages-conversations", city.slug] });
    void queryClient.invalidateQueries({ queryKey: ["board-home", city.slug] });
  }, [city.slug, conversation, queryClient]);

  if (detail.isLoading) {
    return <div className="pb-messages-state" role="status">Loading conversation…</div>;
  }
  if (detail.isError || !conversation) {
    return (
      <div className="pb-messages-state pb-messages-state--error" role="alert">
        <span>The conversation could not be loaded.</span>
        <button type="button" onClick={() => void detail.refetch()}>Retry</button>
      </div>
    );
  }

  const rooms = conversation.kind === "rooms";
  const title = rooms
    ? conversation.facility?.name ?? "Facility"
    : conversation.other?.display ?? "Colleague";

  return (
    <article className="pb-messages-thread-page">
      <Link className="pb-messages-back" to="/board/messages">
        <ArrowLeft size={13} aria-hidden="true" />Messages
      </Link>

      <header className="pb-messages-thread-header">
        <span className="pb-messages-kind" data-kind={conversation.kind} aria-hidden="true">
          {rooms ? <Building2 size={18} /> : <Mail size={18} />}
        </span>
        <div>
          <h1>{title}</h1>
          {rooms ? (
            <p>{conversation.subject ?? "Rooms message"}</p>
          ) : conversation.other ? (
            <p>
              @{conversation.other.handle}
              {conversation.other.specialty ? ` · ${conversation.other.specialty}` : ""}
              <RoleChip role={conversation.other.role} />
            </p>
          ) : null}
        </div>
        <div className="pb-messages-thread-menu">
          <button
            type="button"
            aria-label="Conversation actions"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <MoreVertical size={17} aria-hidden="true" />
          </button>
          {menuOpen ? (
            <div role="menu">
              <button
                type="button"
                role="menuitem"
                disabled={updateConversation.isPending}
                onClick={() =>
                  updateConversation.mutate(
                    { conversationId: conversation.id, muted: !conversation.muted },
                    { onSuccess: () => setMenuOpen(false) },
                  )
                }
              >
                {conversation.muted ? <Volume2 size={14} /> : <BellOff size={14} />}
                {conversation.muted ? "Unmute" : "Mute"}
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={updateConversation.isPending}
                onClick={() =>
                  updateConversation.mutate(
                    { conversationId: conversation.id, archived: !conversation.archived },
                    { onSuccess: () => setMenuOpen(false) },
                  )
                }
              >
                {conversation.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                {conversation.archived ? "Return to inbox" : "Archive"}
              </button>
              {!rooms && conversation.other && !conversation.blocked ? (
                <button
                  type="button"
                  role="menuitem"
                  className="pb-messages-danger-action"
                  disabled={blockUser.isPending}
                  onClick={() => {
                    if (!window.confirm(`Block messages with ${conversation.other?.display ?? "this colleague"}?`)) return;
                    const userId = conversation.other?.userId;
                    if (!userId) return;
                    blockUser.mutate(
                      { userId, blocked: true },
                      {
                        onSuccess: () => {
                          track("message_block", { user_id: userId });
                          setMenuOpen(false);
                        },
                      },
                    );
                  }}
                >
                  <ShieldOff size={14} />Block
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {updateConversation.isError || blockUser.isError ? (
        <p className="pb-messages-form-error" role="alert">
          {messageErrorMessage(updateConversation.error ?? blockUser.error)}
        </p>
      ) : null}

      <section className="pb-messages-thread-body">
        {detail.hasNextPage ? (
          <button
            className="pb-messages-load-older"
            type="button"
            disabled={detail.isFetchingNextPage}
            onClick={() => void detail.fetchNextPage()}
          >
            {detail.isFetchingNextPage ? "Loading…" : "Load older messages"}
          </button>
        ) : null}
        <MessageList messages={messages} />
      </section>

      {rooms ? (
        <aside className="pb-messages-reply-notice pb-messages-reply-notice--thread">
          <Mail size={17} aria-hidden="true" />
          <span><strong>Replies arrive in your email inbox.</strong> This rooms conversation cannot be continued in PulseBoard.</span>
        </aside>
      ) : conversation.blocked ? (
        <p className="pb-messages-blocked" role="status">
          <ShieldOff size={15} aria-hidden="true" />This conversation is blocked. Existing messages remain readable.
        </p>
      ) : (
        <MessageComposer
          onSend={async (body) => {
            const message = await send.mutateAsync({ body });
            track("message_send", {
              conversation_id: conversation.id,
              message_id: message.id,
            });
          }}
        />
      )}
    </article>
  );
}
